import { useState, useEffect, useCallback } from "react";
import { C, Card, CardHead, SectionTitle, Bar, Badge, Btn, Dot } from "./theme";
import { GET } from "./api";
import { useLang } from "./i18n";

const CHART_H = 120;
const CHART_COLORS = { cpu: C.blue, mem: C.purple, gpu: C.green, net_rx: C.cyan, net_tx: C.amber };

function MiniChart({ data, dataKey, color, label, unit = "%", height = CHART_H, fixedMax }) {
  if (!data || data.length === 0) return null;
  const values = data.map(d => d[dataKey] || 0);
  const max = fixedMax || Math.max(...values, 1);
  const w = 100 / Math.max(values.length - 1, 1);

  const points = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * 100;
    const y = height - (v / max) * (height - 20);
    return `${x},${y}`;
  }).join(" ");

  const fillPoints = `0,${height} ${points} 100,${height}`;
  const latest = values[values.length - 1];

  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 9, color: C.dim2, letterSpacing: 1 }}>{label}</span>
        <span style={{ fontSize: 11, color, fontWeight: 700, fontFamily: "monospace" }}>
          {typeof latest === "number" ? latest.toFixed(1) : latest}{unit}
        </span>
      </div>
      <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none"
        style={{ background: C.bg, borderRadius: 6, border: `1px solid ${C.border}` }}>
        <defs>
          <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0.25, 0.5, 0.75].map(p => (
          <line key={p} x1="0" y1={height * (1 - p)} x2="100" y2={height * (1 - p)}
            stroke={C.border} strokeWidth="0.3" />
        ))}
        <polygon points={fillPoints} fill={`url(#grad-${dataKey})`} />
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.2"
          vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

export default function AdminMetrics({ vms, servers, metricsSummary }) {
  const { t } = useLang();
  const [view, setView] = useState("server");
  const [selectedVm, setSelectedVm] = useState(null);
  const [hours, setHours] = useState(1);
  const [history, setHistory] = useState([]);
  const [hostMetrics, setHostMetrics] = useState({});

  const runningVms = vms.filter(v => v.status === "running");

  useEffect(() => {
    if (!selectedVm && runningVms.length > 0) {
      setSelectedVm(runningVms[0].id);
    }
  }, [runningVms, selectedVm]);

  const fetchHistory = useCallback(async () => {
    if (view === "vm" && selectedVm) {
      try {
        const data = await GET(`/api/metrics/${selectedVm}?hours=${hours}`);
        setHistory(data);
      } catch (e) { setHistory([]); }
    }
  }, [selectedVm, hours, view]);

  useEffect(() => {
    fetchHistory();
    const id = setInterval(fetchHistory, 15000);
    return () => clearInterval(id);
  }, [fetchHistory]);

  useEffect(() => {
    const fetchHost = () => GET("/api/host-metrics").then(setHostMetrics).catch(() => {});
    fetchHost();
    const id = setInterval(fetchHost, 10000);
    return () => clearInterval(id);
  }, []);

  return (
    <div>
      {/* Tab: 서버 전체 / VM */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        <Btn small variant={view === "server" ? "cyan" : "ghost"} onClick={() => setView("server")}>
          서버 전체
        </Btn>
        <Btn small variant={view === "vm" ? "cyan" : "ghost"} onClick={() => setView("vm")}>
          VM 메트릭
        </Btn>
      </div>

      {/* ── Server-wide metrics ── */}
      {view === "server" && (
        <>
          <SectionTitle title="서버 리소스 현황" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 14, marginBottom: 20 }}>
            {(servers || []).map(srv => {
              const hm = hostMetrics[srv.id] || {};
              const cpuPct = hm.cpu_pct || 0;
              const memPct = hm.mem_total_mb > 0 ? (hm.mem_used_mb / hm.mem_total_mb * 100) : 0;
              const diskPct = hm.disk_total_gb > 0 ? (hm.disk_used_gb / hm.disk_total_gb * 100) : 0;
              const cpuC = cpuPct > 80 ? C.red : cpuPct > 50 ? C.amber : C.green;
              const memC = memPct > 80 ? C.red : memPct > 50 ? C.amber : C.blue;
              const diskC = diskPct > 80 ? C.red : diskPct > 60 ? C.amber : C.green;
              const loadC = hm.load_1m > (hm.cpu_cores||1) ? C.red : hm.load_1m > (hm.cpu_cores||1)*0.7 ? C.amber : C.green;

              return (
                <Card key={srv.id}>
                  <CardHead>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <Dot color={srv.online ? C.green : C.red} pulse={srv.online} />
                      <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:14, fontWeight:700, color:C.text }}>{srv.label || srv.id}</span>
                    </div>
                  </CardHead>
                  <div style={{ padding: 14 }}>
                    {hm.cpu_cores ? (
                      <>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:6, marginBottom:14 }}>
                          <div style={{ textAlign:"center" }}>
                            <div style={{ fontSize:8, color:C.dim2 }}>CPU ({hm.cpu_cores}c)</div>
                            <div style={{ fontSize:22, fontWeight:700, color:cpuC, fontFamily:"'Rajdhani',sans-serif" }}>{cpuPct.toFixed(0)}%</div>
                          </div>
                          <div style={{ textAlign:"center" }}>
                            <div style={{ fontSize:8, color:C.dim2 }}>RAM</div>
                            <div style={{ fontSize:22, fontWeight:700, color:memC, fontFamily:"'Rajdhani',sans-serif" }}>{memPct.toFixed(0)}%</div>
                            <div style={{ fontSize:8, color:C.dim2 }}>{(hm.mem_used_mb/1024).toFixed(0)}/{(hm.mem_total_mb/1024).toFixed(0)}G</div>
                          </div>
                          <div style={{ textAlign:"center" }}>
                            <div style={{ fontSize:8, color:C.dim2 }}>DISK</div>
                            <div style={{ fontSize:22, fontWeight:700, color:diskC, fontFamily:"'Rajdhani',sans-serif" }}>{diskPct.toFixed(0)}%</div>
                            <div style={{ fontSize:8, color:C.dim2 }}>{hm.disk_used_gb?.toFixed(0)}/{hm.disk_total_gb?.toFixed(0)}G</div>
                          </div>
                          <div style={{ textAlign:"center" }}>
                            <div style={{ fontSize:8, color:C.dim2 }}>LOAD</div>
                            <div style={{ fontSize:22, fontWeight:700, color:loadC, fontFamily:"'Rajdhani',sans-serif" }}>{hm.load_1m?.toFixed(1)}</div>
                            <div style={{ fontSize:8, color:C.dim2 }}>{hm.load_5m?.toFixed(1)} / {hm.load_15m?.toFixed(1)}</div>
                          </div>
                        </div>
                        <div style={{ display:"grid", gap:8 }}>
                          {[
                            { l:"CPU", pct:cpuPct, c:cpuC },
                            { l:"RAM", pct:memPct, c:memC },
                            { l:"DISK", pct:diskPct, c:diskC },
                          ].map(b => (
                            <div key={b.l}>
                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                                <span style={{ fontSize:8, color:C.dim2 }}>{b.l}</span>
                                <span style={{ fontSize:8, color:b.c }}>{b.pct.toFixed(0)}%</span>
                              </div>
                              <Bar pct={b.pct} color={b.c} h={4} />
                            </div>
                          ))}
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between", marginTop:12, fontSize:10, color:C.dim2 }}>
                          <span>VM: <strong style={{color:C.blue}}>{hm.vm_running}</strong>/{hm.vm_total}</span>
                          <span>{hm.ts}</span>
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize:11, color:C.dim2, textAlign:"center", padding:20 }}>메트릭 수집 대기중...</div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* ── VM metrics ── */}
      {view === "vm" && (
        <>
          <SectionTitle title={t("vm_metrics")} />

          {/* VM selector + time range */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            {runningVms.map(vm => (
              <Btn key={vm.id} small
                variant={selectedVm === vm.id ? "cyan" : "ghost"}
                onClick={() => setSelectedVm(vm.id)}>
                {vm.id}
              </Btn>
            ))}
            <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
              {[1, 6, 24, 72].map(h => (
                <Btn key={h} small variant={hours === h ? "blue" : "ghost"}
                  onClick={() => setHours(h)}>
                  {h}h
                </Btn>
              ))}
            </div>
          </div>

      {/* Summary cards for all VMs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, marginBottom: 20 }}>
        {runningVms.map(vm => {
          const m = metricsSummary[vm.id];
          const active = selectedVm === vm.id;
          return (
            <Card key={vm.id} style={{
              padding: 12, cursor: "pointer",
              border: active ? `1px solid ${C.cyan}44` : `1px solid ${C.border}`,
            }} onClick={() => setSelectedVm(vm.id)}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: active ? C.cyan : C.text, fontWeight: 700,
                  fontFamily: "monospace" }}>{vm.id}</span>
                <Dot color={C.green} pulse />
              </div>
              {m ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  <div>
                    <div style={{ fontSize: 8, color: C.dim2 }}>CPU</div>
                    <div style={{ fontSize: 12, color: C.blue, fontWeight: 700 }}>
                      {m.cpu_pct?.toFixed(1)}%
                    </div>
                    <Bar pct={m.cpu_pct || 0} color={C.blue} h={2} />
                  </div>
                  <div>
                    <div style={{ fontSize: 8, color: C.dim2 }}>MEM</div>
                    <div style={{ fontSize: 12, color: C.purple, fontWeight: 700 }}>
                      {m.mem_total_mb > 0 ? ((m.mem_used_mb / m.mem_total_mb) * 100).toFixed(0) : 0}%
                    </div>
                    <Bar pct={m.mem_total_mb > 0 ? (m.mem_used_mb / m.mem_total_mb) * 100 : 0}
                      color={C.purple} h={2} />
                  </div>
                  <div>
                    <div style={{ fontSize: 8, color: C.dim2 }}>GPU</div>
                    <div style={{ fontSize: 12, color: C.green, fontWeight: 700 }}>
                      {m.gpu_util?.toFixed(0) || 0}%
                    </div>
                    <Bar pct={m.gpu_util || 0} color={C.green} h={2} />
                  </div>
                  <div>
                    <div style={{ fontSize: 8, color: C.dim2 }}>TEMP</div>
                    <div style={{ fontSize: 12,
                      color: m.gpu_temp >= 75 ? C.red : m.gpu_temp >= 60 ? C.amber : C.blue,
                      fontWeight: 700 }}>
                      {m.gpu_temp?.toFixed(0) || "--"}C
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 10, color: C.dim2 }}>{t("metrics_wait")}</div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Detailed charts for selected VM */}
      {selectedVm && history.length > 0 && (
        <>
          <SectionTitle title={`${selectedVm} (${hours}h)`} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <Card style={{ padding: 14 }}>
              <MiniChart data={history} dataKey="cpu_pct" color={CHART_COLORS.cpu}
                label={t("cpu_usage")} unit="%" fixedMax={100} />
            </Card>
            <Card style={{ padding: 14 }}>
              <MiniChart data={history.map(d => ({
                ...d, mem_pct: d.mem_total_mb > 0 ? (d.mem_used_mb / d.mem_total_mb) * 100 : 0
              }))}
                dataKey="mem_pct"
                color={CHART_COLORS.mem}
                label={t("mem_usage")}
                unit="%" fixedMax={100} />
            </Card>
            <Card style={{ padding: 14 }}>
              <MiniChart data={history} dataKey="gpu_util" color={CHART_COLORS.gpu}
                label={t("gpu_usage")} unit="%" fixedMax={100} />
            </Card>
            <Card style={{ padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 9, color: C.dim2, letterSpacing: 1 }}>{t("gpu_detail")}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10,
                background: C.bg, borderRadius: 6, border: `1px solid ${C.border}`, padding: 12 }}>
                {(() => {
                  const latest = history[history.length - 1];
                  return [
                    { l: "VRAM", v: latest?.gpu_mem_used_mb > 0
                      ? `${(latest.gpu_mem_used_mb / 1024).toFixed(1)}/${(latest.gpu_mem_total_mb / 1024).toFixed(0)}G`
                      : "--",
                      c: C.blue },
                    { l: "TEMP", v: latest?.gpu_temp > 0 ? `${latest.gpu_temp.toFixed(0)}C` : "--",
                      c: latest?.gpu_temp >= 75 ? C.red : latest?.gpu_temp >= 60 ? C.amber : C.blue },
                    { l: "POWER", v: latest?.gpu_power_w > 0 ? `${latest.gpu_power_w.toFixed(0)}W` : "--",
                      c: C.amber },
                  ].map(s => (
                    <div key={s.l} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 8, color: C.dim2 }}>{s.l}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: s.c,
                        fontFamily: "'Rajdhani',sans-serif" }}>{s.v}</div>
                    </div>
                  ));
                })()}
              </div>
            </Card>
          </div>

          {/* Network chart */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Card style={{ padding: 14 }}>
              <MiniChart data={history.map(d => ({
                ...d, net_rx_mb: (d.net_rx_bytes || 0) / (1024 * 1024)
              }))} dataKey="net_rx_mb" color={CHART_COLORS.net_rx}
                label={t("net_rx")} unit=" MB" />
            </Card>
            <Card style={{ padding: 14 }}>
              <MiniChart data={history.map(d => ({
                ...d, net_tx_mb: (d.net_tx_bytes || 0) / (1024 * 1024)
              }))} dataKey="net_tx_mb" color={CHART_COLORS.net_tx}
                label={t("net_tx")} unit=" MB" />
            </Card>
          </div>
        </>
      )}

      {selectedVm && history.length === 0 && (
        <Card style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 12, color: C.dim2, fontFamily: "monospace" }}>
            {t("no_metrics")}
          </div>
        </Card>
      )}

      {runningVms.length === 0 && (
        <Card style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 12, color: C.dim2, fontFamily: "monospace" }}>
            {t("no_running_vm")}
          </div>
        </Card>
      )}
        </>
      )}
    </div>
  );
}
